/**
 * @ Author: Housefolios
 * @ Create Time: 2023-03-03 16:33:10
 * @ Modified by: David Helmick
 * @ Modified time: 2024-11-20 15:09:02
 * @ Description: Dialog for editing images by cropping or rotating
 */

import React, { useRef, useState, Fragment, useEffect } from 'react'
// Centralized lodash imports - much cleaner!
import {
  map,
  find,
  findIndex,
  pullAt,
  get,
  head,
} from '@/utils/lodash'

import { useMutation } from '@apollo/client/react'

import { Grid, MenuItem, TextField, Box } from '@mui/material'
import Cropper from 'react-easy-crop'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import {
  Close,
  Delete,
  DragIndicator,
  Edit,
} from '@mui/icons-material'
import FlipIcon from '@mui/icons-material/Flip'
import {
  Drawer,
  FormControlLabel,
  Tooltip,
  IconButton,
  List,
  ListItemText,
  ListItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Checkbox,
  Card,
  Slider,
} from '@mui/material'
import {
  DELETE_IMAGE,
  DELETE_IMAGE_MULTI,
  UPDATE_PROPERTY,
} from '@/housefolios-components/PropertyContainer/mutations'
import withSession from '@/housefolios-components/Session/withSession'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'
import { enqueueSnackbar } from 'notistack'
import { IMAGE_UPLOAD } from '@/housefolios-components/TitleButtons/AddPropertyButton/mutations'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'

const ORIENTATION_TO_ANGLE = {
  3: 180,
  6: 90,
  8: -90,
}

function PropertyImages(props) {
  const { imageSrc } = props
  const hostname = import.meta.env.VITE_HOSTNAME

  const [circle, setCircle] = useState(false)

  const [crop, setCrop] = React.useState({ x: 0, y: 0  })
  const [zoom, setZoom] = React.useState(1)
  const [rotation, setRotation] = React.useState(0)
  const [horizontalFlip, setHorizontalFlip] = React.useState(false)
  const [verticalFlip, setVerticalFlip] = React.useState(false)
  const [aspect, setAspect] = React.useState(4 / 3)
  const [croppedAreaPixels, setCroppedAreaPixels] =
    React.useState(null)
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const [replaceImage, setReplaceImage] = useState(false)
  const [resizeImage, setResizeImage] = useState(false)
  const [resizedImage, setResizedImage] = useState(null)

  const changeImage = async (nextImageSrc) => {
    try {
      const resized = await resizeImageFunction(nextImageSrc)
      setResizedImage(resized)
    } catch (error) {
      console.error('Error resizing image:', error)
      // Fallback to original image if resize fails
      setResizedImage(nextImageSrc)
    }
  }

  useEffect(() => {
    // Avoid fetching/cropping remote images while the dialog is closed
    // (parent pages keep this mounted; crossOrigin loads were triggering CORS noise).
    if (!props.open || !imageSrc) return undefined
    changeImage(imageSrc)
    return undefined
  }, [props.open, imageSrc, aspect])

  const handleClose = () => {
    setCrop({ x: 0, y: 0  })
    setZoom(1)
    setRotation(0)
    setHorizontalFlip(false)
    setVerticalFlip(false)
    setCroppedAreaPixels(null)
    setReplaceImage(false)
    setResizeImage(false)
    setResizedImage(null)
    setCircle(false)

    props.handleClose()
  }

  const loadImageElement = (url, useCrossOrigin) =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      if (useCrossOrigin) {
        image.crossOrigin = 'anonymous'
      }
      image.src = url
    })

  const createImage = async (url) => {
    if (!url || typeof url !== 'string') {
      throw new Error('Invalid image URL')
    }

    // data:/blob: URLs are same-origin; no CORS needed.
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      return loadImageElement(url, false)
    }

    // Prefer fetch→blob so we control MIME type (some S3 objects are stored as
    // Content-Type: "png" instead of "image/png", which breaks Image+crossOrigin).
    try {
      const res = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-cache',
      })
      if (!res.ok) {
        throw new Error(`Failed to fetch image (${res.status })`)
      }
      const rawBlob = await res.blob()
      const type =
        rawBlob.type && rawBlob.type.startsWith('image/')
          ? rawBlob.type
          : 'image/png'
      const objectUrl = URL.createObjectURL(
        type === rawBlob.type
          ? rawBlob
          : new Blob([rawBlob], { type  }),
      )
      try {
        return await loadImageElement(objectUrl, false)
      } finally {
        URL.revokeObjectURL(objectUrl)
      }
    } catch (_fetchError) {
      return loadImageElement(url, true)
    }
  }

  function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180
  }

  function rotateSize(width, height, rotation) {
    const rotRad = getRadianAngle(rotation)

    return {
      width:
        Math.abs(Math.cos(rotRad) * width) +
        Math.abs(Math.sin(rotRad) * height),
      height:
        Math.abs(Math.sin(rotRad) * width) +
        Math.abs(Math.cos(rotRad) * height),
    }
  }

  async function getCroppedImg(
    imageSrc,
    pixelCrop,
    rotation = 0,
    flip = { horizontal: false, vertical: false },
  ) {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return null
    }

    const rotRad = getRadianAngle(rotation)

    // calculate bounding box of the rotated image
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
      rotation,
    )

    // set canvas size to match the bounding box
    canvas.width = bBoxWidth
    canvas.height = bBoxHeight

    // translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
    ctx.rotate(rotRad)
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1)
    ctx.translate(-image.width / 2, -image.height / 2)

    // draw rotated image
    ctx.drawImage(image, 0, 0)

    // croppedAreaPixels values are bounding box relative
    // extract the cropped image using these values
    const data = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
    )

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    // paste generated rotate image at the top left corner
    ctx.putImageData(data, 0, 0)

    // As Base64 string
    return canvas.toDataURL('image/png')

    // As a blob
    // return new Promise((resolve, reject) => {
    //   canvas.toBlob((file) => {
    //     resolve(URL.createObjectURL(file))
    //   }, 'image/jpeg')
    // })
  }

  const saveCroppedImage = async () => {
    setCircle(true)
    try {
      const croppedImage = await getCroppedImg(
        resizeImage ? resizedImage : imageSrc,
        croppedAreaPixels,
        rotation,
        { horizontal: horizontalFlip, vertical: verticalFlip },
      )
      await props.saveImage(croppedImage, replaceImage)
      handleClose()
      setCircle(false)
    } catch (e) {
      console.error(e)
      setCircle(false)
    }
  }

  const toggleHorizontalFlip = () => {
    setHorizontalFlip(!horizontalFlip)
    setRotation(360 - rotation)
  }

  const toggleVerticalFlip = () => {
    setVerticalFlip(!verticalFlip)
    setRotation(360 - rotation)
  }

  async function resizeImageFunction(imageSrc) {
    try {
      // Check if imageSrc is valid
      if (!imageSrc || typeof imageSrc !== 'string') {
        console.warn(
          'Invalid imageSrc provided to resizeImageFunction',
        )
        return imageSrc
      }

      const image = await createImage(imageSrc)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        return imageSrc // Return original if canvas context fails
      }

      // Ensure aspect is a valid number
      const currentAspect =
        typeof aspect === 'number' && !isNaN(aspect) ? aspect : 4 / 3

      const imageWider = image.width > image.height

      if (imageWider) {
        canvas.width = image.width
        canvas.height = image.width * (1 / currentAspect)
      } else {
        canvas.width = image.height * currentAspect
        canvas.height = image.height
      }

      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      return canvas.toDataURL()
    } catch (error) {
      console.error('Error in resizeImageFunction:', error)
      return imageSrc // Return original image if resize fails
    }
  }

  return (
    <Dialog
      open={props.open}
      maxWidth="md"
      fullWidth={true}
      onClose={handleClose}
    >
      <BlockUi
        blocking={circle}
        loader={
          <PacmanLoader color={'var(--primary)'} loading={true} />
        }
        message={<Box sx={{ color: '#368cff' }}>Editing Image</Box>}
      >
        <DialogTitle>
          <Typography>Edit Image</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid size={12}>
              <div style={{ position: 'relative', height: 500 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 80,
                  }}
                >
                  <Cropper
                    image={resizeImage ? resizedImage : imageSrc}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    aspect={aspect}
                    transform={`translate(${crop.x}px, ${
                      crop.y
                    }px) rotate(${rotation}deg) scale(${
                      horizontalFlip ? -zoom : zoom
                    }, ${verticalFlip ? -zoom : zoom})`}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: '50%',
                    width: '100%',
                    transform: 'translateX(-50%)',
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Grid
                    container
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-around',
                    }}
                  >
                    <Grid>
                      <Grid
                        container
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Grid>
                          <Typography
                            variant="overline"
                            // classes={{ root: classes.sliderLabel }}
                          >
                            Zoom
                          </Typography>
                        </Grid>
                        <Grid>
                          <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => {
                              setZoom(e.target.value)
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid>
                      <Grid
                        container
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Grid>
                          <Typography
                            variant="overline"
                            // classes={{ root: classes.sliderLabel }}
                          >
                            Rotation
                          </Typography>
                        </Grid>
                        <Grid>
                          <input
                            type="range"
                            value={rotation}
                            min={0}
                            max={360}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => {
                              setRotation(e.target.value)
                            }}
                            //
                          />
                        </Grid>
                        <Grid>
                          <Tooltip title={'Flip Image Horizontally'}>
                            <IconButton
                              onClick={toggleHorizontalFlip}
                              size="large"
                            >
                              <FlipIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                        <Grid>
                          <Tooltip title={'Flip Image Vertically'}>
                            <IconButton
                              style={{ rotate: '90deg' }}
                              onClick={toggleVerticalFlip}
                              size="large"
                            >
                              <FlipIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid>
                      <TextField
                        name="aspect"
                        fullWidth
                        select
                        variant="outlined"
                        label="Aspect Ratio"
                        value={aspect}
                        onChange={(event) =>
                          setAspect(event.target.value)
                        }
                        style={{ width: '200px' }}
                        slotProps={{
                          select: {
                            MenuProps: { style: { width: '100%' } },
                          },
                        }}
                      >
                        <MenuItem key={'4:3'} value={4 / 3}>
                          {'4:3'}
                        </MenuItem>
                        <MenuItem key={'16:9'} value={16 / 9}>
                          {'16:9'}
                        </MenuItem>
                        <MenuItem key={'Square'} value={1}>
                          {'Square'}
                        </MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {props.replaceOption && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={replaceImage}
                  onClick={() => setReplaceImage(!replaceImage)}
                  //value="checked1"
                  color="primary"
                />
              }
              style={{ marginLeft: 5 }}
              label="Replace Image"
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={resizeImage}
                onClick={() => setResizeImage(!resizeImage)}
                //value="checked1"
                color="primary"
              />
            }
            style={{ marginLeft: 5 }}
            label="Resize Image to Aspect Ratio"
          />
          <Typography style={{ flex: 1 }} />
          <Button onClick={handleClose}>
            <Typography>Cancel</Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={saveCroppedImage}
          >
            Save
          </Button>
        </DialogActions>
      </BlockUi>
    </Dialog>
  )
}

export default withSession(PropertyImages)
